/**
 * =========================================================
 * DACI Frontend - Componente de Diálogo Personalizado
 * =========================================================
 * 
 * Componente wrapper que encapsula los componentes Dialog de MUI
 * con soporte automático para tema oscuro y estilos consistentes
 * en toda la aplicación.
 * 
 * Características:
 * - Soporte automático para tema oscuro/claro
 * - Estilos consistentes en toda la aplicación
 * - Fácil mantenimiento centralizado
 * - Compatible con todas las props de MUI Dialog
 */

import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";

/**
 * Componente de diálogo personalizado con soporte para tema oscuro
 * 
 * @param {Object} props - Props del componente
 * @param {Object} props.sx - Estilos adicionales (opcional)
 * @param {...any} rest - Resto de props que se pasan al componente Dialog de MUI
 * @returns {JSX.Element} Componente de diálogo con tema aplicado
 */
const DACIDialog = ({ sx = {}, ...rest }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Importar colors para usar dark.main
  const colors = require("assets/theme/base/colors").default;
  const { dark } = colors;

  // Estilos base para el tema oscuro/claro
  const baseStyles = {
    "& .MuiDialog-paper": {
      backgroundColor: darkMode ? "#202940" : "#ffffff",
      color: darkMode ? "#ffffff" : dark.main,
      ...(sx?.["& .MuiDialog-paper"] || {}), // Merge estilos del paper
    },
    "& .MuiDialogTitle-root": {
      color: darkMode ? "#ffffff" : dark.main,
      ...(sx?.["& .MuiDialogTitle-root"] || {}), // Merge estilos del title
    },
    "& .MuiDialogContentText-root": {
      color: darkMode ? "#ffffff" : dark.main,
      ...(sx?.["& .MuiDialogContentText-root"] || {}), // Merge estilos del content text
    },
    ...Object.keys(sx || {}).reduce((acc, key) => {
      if (!key.startsWith("& .MuiDialog")) {
        acc[key] = sx[key];
      }
      return acc;
    }, {}), // Agregar otros estilos que no sean de los componentes internos
  };

  return (
    <Dialog
      sx={baseStyles}
      {...rest}
    />
  );
};

/**
 * Componente DialogTitle personalizado con soporte para tema oscuro
 */
const DACIDialogTitle = ({ sx = {}, ...rest }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const colors = require("assets/theme/base/colors").default;
  const { dark } = colors;

  const baseStyles = {
    color: darkMode ? "#ffffff" : dark.main,
    ...sx,
  };

  return <DialogTitle sx={baseStyles} {...rest} />;
};

/**
 * Componente DialogContent personalizado con soporte para tema oscuro
 */
const DACIDialogContent = ({ sx = {}, ...rest }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const colors = require("assets/theme/base/colors").default;
  const { dark } = colors;

  const baseStyles = {
    color: darkMode ? "#ffffff" : dark.main,
    ...sx,
  };

  return <DialogContent sx={baseStyles} {...rest} />;
};

/**
 * Componente DialogContentText personalizado con soporte para tema oscuro
 */
const DACIDialogContentText = ({ sx = {}, ...rest }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const colors = require("assets/theme/base/colors").default;
  const { dark } = colors;

  const baseStyles = {
    color: darkMode ? "#ffffff" : dark.main,
    ...sx,
  };

  return <DialogContentText sx={baseStyles} {...rest} />;
};

/**
 * Componente DialogActions personalizado con soporte para tema oscuro
 */
const DACIDialogActions = ({ sx = {}, ...rest }) => {
  return <DialogActions sx={sx} {...rest} />;
};

// PropTypes
DACIDialog.propTypes = {
  sx: PropTypes.object,
};

DACIDialogTitle.propTypes = {
  sx: PropTypes.object,
};

DACIDialogContent.propTypes = {
  sx: PropTypes.object,
};

DACIDialogContentText.propTypes = {
  sx: PropTypes.object,
};

DACIDialogActions.propTypes = {
  sx: PropTypes.object,
};

// Valores por defecto
DACIDialog.defaultProps = {
  sx: {},
};

DACIDialogTitle.defaultProps = {
  sx: {},
};

DACIDialogContent.defaultProps = {
  sx: {},
};

DACIDialogContentText.defaultProps = {
  sx: {},
};

DACIDialogActions.defaultProps = {
  sx: {},
};

export {
  DACIDialog,
  DACIDialogTitle,
  DACIDialogContent,
  DACIDialogContentText,
  DACIDialogActions,
};

export default DACIDialog;